// Contabilidad — DATOS REALES por cooperativa, desde el motor contable del backend:
// comprobantes (con notas contables y anulación), libro diario, libro auxiliar por
// tercero, plan de cuentas, períodos y cierre del ejercicio con los fondos del Art. 76.
// Antes usaba el motor local de JS y datos de ejemplo. Toda operación exige un período
// abierto para su fecha; si está cerrado, el backend lo dice y el mensaje se muestra.

import React, { useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandLess, ExpandMore } from '@mui/icons-material';
import EncabezadoOperativo from '../components/EncabezadoOperativo';
import FormularioDialog from '../components/FormularioDialog';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import { contabilidadApi, fondosApi, periodosApi, tercerosApi } from '../services/coopnetApi';
import { fecha, hoy, moneda } from '../utils/formato';

const TIPO_COMPROBANTE = { RC: 'Recibo de caja', CE: 'Comprobante de egreso', NC: 'Nota contable' };
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const Cargando = () => <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} /></Box>;

// ───────────────── Períodos ─────────────────
function TabPeriodos({ id }) {
  const { datos, cargando, error, recargar } = useCargar(() => periodosApi.listar(id), [id], []);
  const [abrir, setAbrir] = useState(false);
  const [msg, setMsg] = useState('');
  const esAbierto = (p) => String(p.estado).toLowerCase() === 'abierto';

  const cerrar = async (p) => {
    if (!window.confirm(`¿Cerrar el período ${p.clave}? No se podrán registrar más operaciones con fecha de ese mes.`)) return;
    try { await periodosApi.cerrar(id, p.anio, p.mes); setMsg(''); recargar(); } catch (e) { setMsg(e.message); }
  };

  return (
    <>
      <Box sx={{ mb: 2 }}><Button variant="contained" startIcon={<AddIcon />} onClick={() => setAbrir(true)}>Abrir período</Button></Box>
      {(error || msg) && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg || error}</Alert>}
      {cargando ? <Cargando /> : (
        <TablaDatos
          filas={[...datos].sort((a, b) => b.anio - a.anio || b.mes - a.mes)}
          claveFila={(p) => p.clave}
          vacio="No hay períodos. Abre el del mes en curso para poder registrar operaciones."
          columnas={[
            { titulo: 'Período', render: (p) => `${MESES[p.mes - 1]} ${p.anio}` },
            { titulo: 'Estado', render: (p) => <Chip size="small" color={esAbierto(p) ? 'success' : 'default'} label={esAbierto(p) ? 'Abierto' : 'Cerrado'} /> },
            { titulo: 'Cierre', render: (p) => (p.fechaCierre ? fecha(p.fechaCierre) : '—') },
            { titulo: '', render: (p) => (esAbierto(p) ? <Button size="small" color="warning" onClick={() => cerrar(p)}>Cerrar período</Button> : null) },
          ]}
        />
      )}
      <FormularioDialog
        abierto={abrir}
        titulo="Abrir período contable"
        campos={[
          { campo: 'anio', label: 'Año', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'mes', label: 'Mes', tipo: 'select', requerido: true, opciones: MESES.map((m, i) => ({ valor: i + 1, etiqueta: m })) },
        ]}
        inicial={{ anio: new Date().getFullYear(), mes: new Date().getMonth() + 1 }}
        textoBoton="Abrir"
        onGuardar={async (v) => { await periodosApi.abrir(id, v.anio, Number(v.mes)); recargar(); }}
        onClose={() => setAbrir(false)}
      />
    </>
  );
}

// ───────────────── Nota contable ─────────────────
function NotaContableDialog({ abierto, id, onClose, onGuardado }) {
  const { datos } = useCargar(async () => (abierto ? { plan: await contabilidadApi.planCuentas(id), terceros: await tercerosApi.listar(id) } : null), [abierto, id]);
  const [concepto, setConcepto] = useState('');
  const [soporte, setSoporte] = useState('');
  const [lineas, setLineas] = useState([{ cuenta: null, debito: '', credito: '', terceroId: '' }, { cuenta: null, debito: '', credito: '', terceroId: '' }]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  const plan = datos?.plan || [];
  const terceros = datos?.terceros || [];
  const totalD = lineas.reduce((t, l) => t + (Number(l.debito) || 0), 0);
  const totalC = lineas.reduce((t, l) => t + (Number(l.credito) || 0), 0);
  const cuadra = totalD > 0 && Math.abs(totalD - totalC) < 0.005;
  const completas = lineas.every((l) => l.cuenta && ((Number(l.debito) || 0) > 0) !== ((Number(l.credito) || 0) > 0));
  const set = (i, parcial) => setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, ...parcial } : l)));

  const guardar = async () => {
    setEnviando(true); setError('');
    try {
      await contabilidadApi.emitirNotaContable(id, {
        concepto: concepto.trim(), soporte: soporte.trim() || null,
        movimientos: lineas.map((l) => ({ cuenta: l.cuenta.codigo, debito: Number(l.debito) || 0, credito: Number(l.credito) || 0, terceroId: l.terceroId || null })),
      });
      setConcepto(''); setSoporte(''); setLineas([{ cuenta: null, debito: '', credito: '', terceroId: '' }, { cuenta: null, debito: '', credito: '', terceroId: '' }]);
      onGuardado(); onClose();
    } catch (e) { setError(e.message || 'No se pudo emitir la nota contable.'); } finally { setEnviando(false); }
  };

  return (
    <Dialog open={Boolean(abierto)} onClose={enviando ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Nueva nota contable</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        <TextField label="Concepto *" value={concepto} onChange={(e) => setConcepto(e.target.value)} fullWidth />
        <TextField label="Soporte (referencia del documento)" value={soporte} onChange={(e) => setSoporte(e.target.value)} fullWidth />
        {lineas.map((l, i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '2.4fr 1fr 1fr 1.6fr auto', gap: 1, alignItems: 'center' }}>
            <Autocomplete size="small" options={plan} value={l.cuenta} onChange={(_, v) => set(i, { cuenta: v })}
              getOptionLabel={(o) => `${o.codigo} — ${o.nombre}`} isOptionEqualToValue={(a, b) => a.codigo === b.codigo}
              renderInput={(p) => <TextField {...p} label="Cuenta" />} />
            <TextField size="small" type="number" label="Débito" value={l.debito} onChange={(e) => set(i, { debito: e.target.value, credito: e.target.value ? '' : l.credito })} />
            <TextField size="small" type="number" label="Crédito" value={l.credito} onChange={(e) => set(i, { credito: e.target.value, debito: e.target.value ? '' : l.debito })} />
            <FormControl size="small">
              <InputLabel>Tercero</InputLabel>
              <Select label="Tercero" value={l.terceroId} onChange={(e) => set(i, { terceroId: e.target.value })}>
                <MenuItem value="">—</MenuItem>
                {terceros.map((t) => <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>)}
              </Select>
            </FormControl>
            <IconButton size="small" disabled={lineas.length <= 2} onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
        ))}
        <Box><Button size="small" startIcon={<AddIcon />} onClick={() => setLineas((ls) => [...ls, { cuenta: null, debito: '', credito: '', terceroId: '' }])}>Agregar línea</Button></Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
          <Typography variant="body2">Débitos: <b>{moneda(totalD)}</b></Typography>
          <Typography variant="body2">Créditos: <b>{moneda(totalC)}</b></Typography>
          <Chip size="small" color={cuadra ? 'success' : 'warning'} label={cuadra ? 'Cuadra' : 'No cuadra'} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={!concepto.trim() || !cuadra || !completas || enviando}>Emitir nota contable</Button>
      </DialogActions>
    </Dialog>
  );
}

// ───────────────── Comprobantes ─────────────────
function TabComprobantes({ id }) {
  const { datos, cargando, error, recargar } = useCargar(() => contabilidadApi.comprobantes(id), [id], []);
  const [nota, setNota] = useState(false);
  const [anular, setAnular] = useState(null);
  return (
    <>
      <Box sx={{ mb: 2 }}><Button variant="contained" startIcon={<AddIcon />} onClick={() => setNota(true)}>Nueva nota contable</Button></Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {cargando ? <Cargando /> : (
        <TablaDatos
          filas={datos}
          vacio="Aún no hay comprobantes."
          columnas={[
            { titulo: 'Número', render: (c) => c.numero },
            { titulo: 'Tipo', render: (c) => TIPO_COMPROBANTE[c.tipo] || c.tipo },
            { titulo: 'Fecha', render: (c) => fecha(c.fecha) },
            { titulo: 'Concepto', render: (c) => c.concepto },
            { titulo: 'Estado', render: (c) => <Chip size="small" color={c.estado === 'Anulado' ? 'default' : 'success'} label={c.estado} /> },
            { titulo: '', render: (c) => (c.estado !== 'Anulado' ? <Button size="small" color="error" onClick={() => setAnular(c)}>Anular</Button> : <Typography variant="caption" color="text.secondary">{c.motivoAnulacion}</Typography>) },
          ]}
        />
      )}
      <NotaContableDialog abierto={nota} id={id} onClose={() => setNota(false)} onGuardado={recargar} />
      <FormularioDialog
        abierto={Boolean(anular)}
        titulo={`Anular comprobante ${anular?.numero ?? ''}`}
        descripcion="Se genera el asiento de reversión; el comprobante original queda como anulado."
        campos={[{ campo: 'motivo', label: 'Motivo de la anulación', tipo: 'area', requerido: true }]}
        textoBoton="Anular"
        onGuardar={async (v) => { await contabilidadApi.anularComprobante(id, anular.id, v.motivo); recargar(); }}
        onClose={() => setAnular(null)}
      />
    </>
  );
}

// ───────────────── Libro diario ─────────────────
function TabLibroDiario({ id }) {
  const { datos, cargando, error } = useCargar(() => contabilidadApi.libroDiario(id), [id], []);
  const [abiertos, setAbiertos] = useState(() => new Set());
  const alternar = (k) => setAbiertos((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  if (cargando) return <Cargando />;
  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TablaDatos
        filas={datos}
        vacio="El libro diario está vacío."
        columnas={[
          { titulo: '', render: (a) => <IconButton size="small" onClick={() => alternar(a.id)}>{abiertos.has(a.id) ? <ExpandLess /> : <ExpandMore />}</IconButton> },
          { titulo: 'Fecha', render: (a) => fecha(a.fecha) },
          { titulo: 'Concepto', render: (a) => (
            <Box>
              {a.concepto}
              {abiertos.has(a.id) && (
                <Box sx={{ mt: 1, pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                  {(a.movimientos || []).map((m) => (
                    <Typography key={m.id} variant="caption" display="block">
                      {m.cuentaCodigo} · {m.debito ? `Débito ${moneda(m.debito)}` : `Crédito ${moneda(m.credito)}`}{m.terceroId ? ` · ${m.terceroId}` : ''}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ) },
          { titulo: 'Referencia', render: (a) => a.referencia },
          { titulo: 'Débitos', align: 'right', render: (a) => moneda(a.totalDebito) },
          { titulo: 'Créditos', align: 'right', render: (a) => moneda(a.totalCredito) },
        ]}
      />
    </>
  );
}

// ───────────────── Libro auxiliar ─────────────────
function TabAuxiliar({ id }) {
  const { datos: cuentas } = useCargar(() => contabilidadApi.cuentasConAuxiliar(id), [id], []);
  const { datos: plan } = useCargar(() => contabilidadApi.planCuentas(id), [id], []);
  const [codigo, setCodigo] = useState('');
  const { datos, cargando, error } = useCargar(() => (codigo ? contabilidadApi.libroAuxiliar(id, codigo) : []), [id, codigo], []);
  const nombre = useMemo(() => new Map(plan.map((p) => [p.codigo, p.nombre])), [plan]);
  return (
    <>
      <FormControl size="small" sx={{ minWidth: 340, mb: 2 }}>
        <InputLabel>Cuenta</InputLabel>
        <Select label="Cuenta" value={codigo} onChange={(e) => setCodigo(e.target.value)}>
          {cuentas.map((c) => <MenuItem key={c} value={c}>{c} — {nombre.get(c) || ''}</MenuItem>)}
        </Select>
      </FormControl>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {cargando ? <Cargando /> : (
        <TablaDatos
          filas={datos}
          claveFila={(f) => f.tercero}
          vacio={codigo ? 'Esta cuenta no tiene movimientos por tercero.' : cuentas.length ? 'Elige una cuenta para ver su auxiliar por tercero.' : 'Aún no hay cuentas con auxiliar.'}
          columnas={[
            { titulo: 'Tercero', render: (f) => f.tercero },
            { titulo: 'Débitos', align: 'right', render: (f) => moneda(f.totalDebito) },
            { titulo: 'Créditos', align: 'right', render: (f) => moneda(f.totalCredito) },
            { titulo: 'Saldo', align: 'right', render: (f) => <b>{moneda(f.saldo)}</b> },
          ]}
        />
      )}
    </>
  );
}

// ───────────────── Plan de cuentas ─────────────────
function TabPlan({ id }) {
  const { datos, cargando, error } = useCargar(() => contabilidadApi.planCuentas(id), [id], []);
  const [q, setQ] = useState('');
  const filas = useMemo(() => { const t = q.trim().toLowerCase(); return t ? datos.filter((c) => `${c.codigo} ${c.nombre}`.toLowerCase().includes(t)) : datos; }, [datos, q]);
  return (
    <>
      <TextField size="small" placeholder="Buscar por código o nombre" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 320, mb: 2 }} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {cargando ? <Cargando /> : (
        <TablaDatos filas={filas} claveFila={(c) => c.codigo} columnas={[
          { titulo: 'Código', render: (c) => c.codigo },
          { titulo: 'Nombre', render: (c) => c.nombre },
          { titulo: 'Clase', render: (c) => c.clase },
          { titulo: 'Naturaleza', render: (c) => c.naturaleza },
        ]} />
      )}
    </>
  );
}

// ───────────────── Cierre y fondos ─────────────────
function TabCierre({ id }) {
  const { datos, cargando, error } = useCargar(() => fondosApi.especiales(id), [id], []);
  const [dlg, setDlg] = useState(null);
  const [ok, setOk] = useState('');
  return (
    <>
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Typography variant="h6" gutterBottom>Fondos especiales (Art. 76)</Typography>
      {cargando ? <Cargando /> : (
        <TablaDatos filas={datos} vacio="No hay fondos especiales configurados." columnas={[
          { titulo: 'Fondo', render: (f) => f.nombre },
          { titulo: 'Porcentaje del excedente', align: 'right', render: (f) => `${f.porcentaje}%` },
        ]} />
      )}
      <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={() => setDlg('cierre')}>Cerrar ejercicio</Button>
        <Button variant="outlined" onClick={() => setDlg('distribucion')}>Distribuir excedentes</Button>
      </Box>
      <FormularioDialog
        abierto={dlg === 'cierre'}
        titulo="Cierre del ejercicio"
        descripcion="Traslada el resultado del ejercicio a excedentes. Genera comprobante."
        campos={[{ campo: 'fecha', label: 'Fecha de cierre', tipo: 'fecha', requerido: true }]}
        inicial={{ fecha: hoy() }}
        textoBoton="Cerrar ejercicio"
        onGuardar={async (v) => { await fondosApi.cierreEjercicio(id, { fecha: v.fecha }); setOk('Ejercicio cerrado.'); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg === 'distribucion'}
        titulo="Distribución de excedentes"
        descripcion="Reparte el monto entre los fondos especiales según sus porcentajes."
        campos={[
          { campo: 'monto', label: 'Monto a distribuir', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ fecha: hoy() }}
        textoBoton="Distribuir"
        onGuardar={async (v) => { await fondosApi.distribuirExcedentes(id, { monto: v.monto, fecha: v.fecha }); setOk('Excedentes distribuidos.'); }}
        onClose={() => setDlg(null)}
      />
    </>
  );
}

const TABS = [['Comprobantes', TabComprobantes], ['Libro diario', TabLibroDiario], ['Libro auxiliar', TabAuxiliar], ['Plan de cuentas', TabPlan], ['Períodos', TabPeriodos], ['Cierre y fondos', TabCierre]];

const Contabilidad = () => {
  const op = useCooperativaOperativa();
  const [tab, setTab] = useState(0);
  const Actual = TABS[tab][1];
  return (
    <EncabezadoOperativo titulo="Contabilidad" subtitulo="Contabilidad de la cooperativa (PUC)" operativa={op}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        {TABS.map(([titulo]) => <Tab key={titulo} label={titulo} />)}
      </Tabs>
      <Actual key={`${op.cooperativaId}-${tab}`} id={op.cooperativaId} />
    </EncabezadoOperativo>
  );
};

export default Contabilidad;
